import React, { memo } from 'react'
import clsx from 'clsx'
import { Box, Container, Grid, ThemeProvider } from '@material-ui/core'
import EditableHTML from '@v4/components/common/EditableHTML'
import SortHandler from '@v4/components/common/SortHandler'
import Image from '@v4/components/common/Image'
import BackwardsAction from './Components/BackwardsAction'
import AnswerType from './Components/AnswerTypes'
import Media from './Components/Media'
import WhiteThemeProvider from './Components/WhiteThemeProvider'
import CategoryInfo from './Components/CategoryInfo'
import Instructions from './Components/Instructions'
import { useQuestion } from './Question.hooks'

const Question = () => {
	const [
		{
			classes,
			withShadow,
			align,
			backButtonLabel,
			sectionTheme,
			backwardsActionTheme,
			categoryInfoTheme,
			instructionsTheme,
			questionTheme,
			useOldCheckboxes,
			showCategory,
			withBgImage,
			textSize,
			locked,
			questionId,
			instructionPosition,
			visibleInstruction,
			visible,
			type,
			lockedQuestionTitle,
			lockedQuestionText,
			assetMetaData,

			invertColors,
			shading,
			key,
			direction,
			position,
			singleGridItemProps,
			isSingleGridItem,
			showTopCover,
			isContainedPosition,
			showMetaBgImage,
			isCoverPosition,
			metaBackgroundImage,
			isBackground,
			aboveAndBelowObjectFit,
			aboveOrBelow,
			gridKey,
			autoplay,
			showMedia,
			withOldTextSize,
		},
		{ handleLockClick },
	] = useQuestion()

	const instructionEl = visibleInstruction ? (
		<ThemeProvider theme={instructionsTheme}>
			<WhiteThemeProvider>
				<Instructions
					key={questionId}
					questionId={questionId}
					align={align}
					withShadow={withShadow}
					className="question-instruction"
				/>
			</WhiteThemeProvider>
		</ThemeProvider>
	) : null

	return visible ? (
		<ThemeProvider theme={sectionTheme}>
			<Box
				className={clsx(classes.root, 'question')}
				data-question-id={questionId}
				display="flex"
				alignItems="center"
			>
				{showMetaBgImage && (
					<Image
						className={clsx(classes.background, classes.overlayOpacity, 'question-background-image')}
						metaData={metaBackgroundImage}
						isBackground
					/>
				)}

				{isCoverPosition && (
					<div className={clsx(classes.cover, 'question-media-cover')}>
						<Media
							shading={shading}
							metaData={assetMetaData}
							objectFit="cover"
							align={align}
							isBackground
							autoplay={autoplay}
						/>
					</div>
				)}

				{isBackground && (
					<div className={clsx(classes.background, 'question-media-background')}>
						<Media
							shading={shading}
							align={align}
							isBackground
							metaData={assetMetaData}
							autoplay={autoplay}
						/>
					</div>
				)}

				{showTopCover && (
					<div className={clsx(classes.topCoverWrapper, 'question-media-top-cover')}>
						<Media
							shading={shading}
							align={align}
							isBackground
							metaData={assetMetaData}
							autoplay={autoplay}
						/>
					</div>
				)}

				{locked ? (
					<Box className={clsx(classes.lock, 'question-locked')} onClick={handleLockClick}>
						{lockedQuestionTitle && <EditableHTML variant="h4" html={lockedQuestionTitle} />}
						{lockedQuestionText && <EditableHTML html={lockedQuestionText} />}
					</Box>
				) : (
					<div className={classes.wrapper}>
						<Grid
							key={gridKey}
							className={clsx(classes.content, 'question-content')}
							component={Container}
							container
							justify={align || undefined}
						>
							<SortHandler key={key} direction={direction}>
								<Grid
									item
									className={clsx(classes.gridItem, classes.gridContained)}
									{...singleGridItemProps}
								>
									<ThemeProvider theme={backwardsActionTheme}>
										<WhiteThemeProvider>
											<BackwardsAction
												label={backButtonLabel}
												align={align}
												className="question-backward"
												withShadow={withShadow}
											/>
										</WhiteThemeProvider>
									</ThemeProvider>
									<ThemeProvider theme={categoryInfoTheme}>
										<CategoryInfo
											showCategory={showCategory}
											align={align}
											withShadow={withShadow}
										/>
									</ThemeProvider>

									<ThemeProvider theme={questionTheme}>
										<AnswerType
											key={questionId}
											withBgImage={withBgImage}
											align={align}
											withShadow={withShadow}
											useOldCheckboxes={useOldCheckboxes}
											textSize={textSize}
											questionId={questionId}
											type={type}
											instruction={instructionEl}
											instructionPosition={instructionPosition}
											mediaPosition={position}
											showMedia={showMedia}
											invertColors={invertColors}
											withOldTextSize={withOldTextSize}
											media={
												aboveOrBelow ? (
													<div className={classes.aboveAndBelowWrapper}>
														<div
															className={clsx(
																classes.aboveAndBelow,
																'question-media-below-above',
															)}
														>
															<Media
																align={align}
																objectFit={aboveAndBelowObjectFit}
																metaData={assetMetaData}
																autoplay={autoplay}
															/>
														</div>
													</div>
												) : null
											}
										/>
									</ThemeProvider>
								</Grid>
								{!isSingleGridItem && (
									<Grid
										item
										lg={6}
										md={6}
										sm={12}
										xs={12}
										className={clsx(classes.gridItem, isContainedPosition && classes.desktopImage)}
									>
										{isContainedPosition && (
											<div className={clsx(classes.contain, 'question-media-contain')}>
												<Media
													objectFit="contain"
													align={align}
													isBackground
													metaData={assetMetaData}
													autoplay={autoplay}
												/>
											</div>
										)}
									</Grid>
								)}
							</SortHandler>
						</Grid>
					</div>
				)}
			</Box>
		</ThemeProvider>
	) : null
}

export default memo(Question)
